'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { BookDemoButton } from '@/components/ui';
import { useTranslation } from '@/hooks/useTranslation';
import { HERO_SIZES } from '@/lib/responsiveImage';
import s from './HeroTwo.module.css';

/*
 * HeroTwo — two-column hero on the Aurora background (bg unchanged).
 * Motion is CSS-only (no framer-motion) so the critical homepage chunk
 * stays smaller for TBT / unused-JS.
 *
 * LCP: next/image + custom static loader serves -400w/-640w/-960w variants.
 */

const SLIDES = ['/locobuzz-hero-image-1.webp', '/locobuzz-hero-image-2.webp'];

export function HeroTwo() {
  const { t } = useTranslation();
  const words = [t('hero.capsule_word_1'), t('hero.capsule_word_2'), t('hero.capsule_word_3')];
  const [wi, setWi] = useState(0);
  const [si, setSi] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const id = setInterval(() => setWi((i) => (i + 1) % words.length), 2200);
    return () => clearInterval(id);
  }, [words.length]);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return undefined;
    const id = setInterval(() => setSi((i) => (i + 1) % SLIDES.length), 2800);
    return () => clearInterval(id);
  }, []);

  return (
    <section className={s.hero}>
      <div className={s.content}>
        <div className={`${s.left} ${s.leftEnter}`}>
          <h1 className={s.h1}>
            <span className={s.h1Sans}>{t('hero.tagline_part_1')}{' '}</span>
            <span className={s.h1Accent}>{t('hero.tagline_accent')}</span>
            {' '}
            <span className={s.h1Sans}>{t('hero.tagline_part_2')}</span>{' '}
            <span className={s.intoRot}>
              {t('hero.tagline_into') ? (
                <>
                  <span className={s.h1Sans}>{t('hero.tagline_into')}</span>{' '}
                </>
              ) : null}
              <span key={wi} className={s.rotWord}>
                {words[wi]}
              </span>
            </span>
          </h1>

          <p className={s.subParagraph}>{t('hero.sub_paragraph')}</p>

          <div className={s.ctaRow}>
            <BookDemoButton label={t('hero.cta_book_demo')} href="#book-demo" />
          </div>
        </div>

        <div className={s.right} aria-hidden="true">
          <div className={s.slideStage}>
            {SLIDES.map((slideSrc, i) => (
              <Image
                key={slideSrc}
                src={slideSrc}
                alt=""
                width={960}
                height={960}
                sizes={HERO_SIZES}
                priority={i === 0}
                fetchPriority={i === 0 ? 'high' : 'auto'}
                loading={i === 0 ? undefined : 'lazy'}
                draggable={false}
                className={`${s.slide} ${i === si ? s.slideActive : ''}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
